import { Flex, useToast } from "@chakra-ui/react";
import type { JSONSchema7 } from "json-schema";
import { useContext, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProjectRead } from "@nens/rana/api-v1";
import { RanaButton } from "@nens/ranalib";
import { useRunOnceWhen } from "@nens/react/hooks";

import { BackButton } from "@/components/back-button";
import { ProcessRun, ProcessRunProps } from "@/features/project-process-run";
import { DatasetValue } from "@/features/project-process-run-form-dataset-selector";
import { useProjectActionAllowed } from "@/hooks/use-action-allowed-in-project";
import { useQueryState } from "@/hooks/use-query-search-state";
import { useGetJobQuery } from "@/queries/query-job";
import { useProcessByTagQuery } from "@/queries/query-process";
import { ProjectContext, ProjectProvider } from "@/stores/project-provider";

import { DatasetDetailExtractOpenModal, DatasetDetailExtractProps } from "./dataset-detail-extract";

interface ExtractProcessRunInModalProps extends DatasetDetailExtractProps {
  setOpenModal: (openModal: DatasetDetailExtractOpenModal) => void;
  project: ProjectRead;
}

export const ExtractProcessRunInModal = ({
  dataset,
  project,
  setOpenModal,
  goToExtractDatasetProcess,
}: ExtractProcessRunInModalProps) => {
  const processQuery = useProcessByTagQuery("extract_dataset");
  const process = useMemo(() => {
    const process = structuredClone(processQuery.data);

    const setDatasetSchema = <K extends keyof JSONSchema7>(key: K, value: JSONSchema7[K]) => {
      if (process?.inputSchema.properties) process.inputSchema.properties.dataset[key] = value;
    };

    const constValue: DatasetValue = { id: dataset.id, input_type: "dataset" };

    setDatasetSchema("const", constValue);
    setDatasetSchema("readOnly", true);

    return process;
  }, [dataset.id, processQuery.data]);

  const getJobQuery = useGetJobQuery();

  const { data: job, setData: setJob } = useQueryState(getJobQuery);
  const { data: sourceJob, setData: setSourceJob } = useQueryState(getJobQuery);

  const changeCurrentJobToSourceJob = () => {
    if (!job) {
      console.warn("No job provided to changeCurrentJobToSourceJob");
      return;
    }
    setSourceJob(job);
    setJob(undefined);
  };

  return (
    <ProjectProvider project={project}>
      <ExtractProcessRunInModalInProjectProvider
        {...{
          dataset,
          setOpenModal,
          goToExtractDatasetProcess,
          job,
          setJob,
          process,
          sourceJob,
          changeCurrentJobToSourceJob,
        }}
      />
    </ProjectProvider>
  );
};

type ExtractProcessRunInModalInProjectProviderProps = Pick<
  ExtractProcessRunInModalProps,
  "dataset" | "setOpenModal" | "goToExtractDatasetProcess"
> &
  ProcessRunProps;

// Show error toast and go back if user does not have write permissions (user role === visitor in project)
const ExtractProcessRunInModalInProjectProvider = ({
  dataset,
  setOpenModal,
  goToExtractDatasetProcess,
  job,
  setJob,
  process,
  sourceJob,
  changeCurrentJobToSourceJob,
}: ExtractProcessRunInModalInProjectProviderProps) => {
  const toast = useToast();
  const intl = useIntl();
  const { canWriteProject, permissionsLoading } = useProjectActionAllowed();
  const project = useContext(ProjectContext);

  useRunOnceWhen(!permissionsLoading && !canWriteProject, () => {
    toast({
      status: "error",
      description: intl.formatMessage(
        {
          id: "no.project.permission.write",
          defaultMessage: "You do not have permission to write to project: {projectName}.",
        },
        { projectName: project.name }
      ),
    });
    setOpenModal({ type: "project" });
  });

  return (
    <>
      <Flex justifyContent="space-between">
        <BackButton
          back={() => {
            setOpenModal({ type: "project" });
          }}
        >
          <FormattedMessage
            id="button.choose_other_project"
            defaultMessage="Choose other project"
          />
        </BackButton>
        {job ? (
          <RanaButton
            variant="secondary"
            onClick={() =>
              goToExtractDatasetProcess({ projectSlug: project.slug + "", jobId: job.id })
            }
          >
            <FormattedMessage
              id="button.open_process_run_in_project"
              defaultMessage="Open process run in project"
            />
          </RanaButton>
        ) : (
          <RanaButton
            variant="secondary"
            onClick={() =>
              goToExtractDatasetProcess({ projectSlug: project.slug + "", datasetId: dataset.id })
            }
          >
            <FormattedMessage
              id="button.open_process_in_project"
              defaultMessage="Open process in project"
            />
          </RanaButton>
        )}
      </Flex>
      <ProcessRun
        process={permissionsLoading ? undefined : process}
        setJob={setJob}
        job={job}
        sourceJob={sourceJob}
        changeCurrentJobToSourceJob={changeCurrentJobToSourceJob}
      />
    </>
  );
};
